import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import qapi from 'views_shared/app/javascript/api';
import qlog from 'views_shared/app/javascript/scripts/qlog';
import { requestUrl } from 'views_shared/app/javascript/utils/urls';

import CollapsibleSection from '../CollapsibleSection';

function PitchesSection({ profileSlug, conversationPitchId = null }) {
  const [pitches, setPitches] = useState([]);

  useEffect(() => {
    setPitches([]);

    if (!profileSlug) return undefined;

    let cancelled = false;

    const fetchPitches = async () => {
      try {
        // Fetch top-N for the sidebar plus, if this is a pitch-origin conversation,
        // the conversation's own pitch — guaranteed to appear first even if it falls
        // outside the top-N window for high-pitch users.
        const [listResult, originResult] = await Promise.all([
          qapi.pitches.getByProfileSlug(profileSlug),
          conversationPitchId ? qapi.pitches.get(conversationPitchId) : Promise.resolve(null),
        ]);
        if (cancelled) return;

        if (listResult.error) {
          qlog.error('Failed to fetch pitches:', listResult.error);
          return;
        }

        const list = Array.isArray(listResult.data) ? listResult.data : [];
        const originPitch = originResult && !originResult.error && originResult.data?.[0];

        if (originPitch) {
          const rest = list.filter((p) => p.id !== originPitch.id);
          setPitches([originPitch, ...rest]);
        } else {
          setPitches(list);
        }
      } catch (error) {
        if (!cancelled) {
          qlog.error('Failed to fetch pitches:', error);
        }
      }
    };

    fetchPitches();

    return () => {
      cancelled = true;
    };
  }, [profileSlug, conversationPitchId]);

  if (pitches.length === 0) return null;

  return (
    <CollapsibleSection title="Pitches">
      <div className="p-3 pt-0">
        {pitches.map((pitch, index) => (
          <a
            key={pitch.id}
            href={requestUrl({ slug: pitch.request_slug, migrate_id: pitch.request_migrate_id })}
            target="_blank"
            rel="noopener noreferrer"
            className={classnames(
              'd-flex align-items-center text-decoration-none paragraph-sm text-primary-60 sidebar-pitch-link',
              { 'mb-2': index !== pitches.length - 1 },
            )}
          >
            <i className="fa-solid fa-megaphone me-2" />
            <span className="flex-grow-1 text-truncate">{pitch.request_title}</span>
          </a>
        ))}
      </div>
    </CollapsibleSection>
  );
}

PitchesSection.propTypes = {
  profileSlug: PropTypes.string.isRequired,
  conversationPitchId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default PitchesSection;
